import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { loadArchivedCPRs, restoreArchivedCPR, clearHistory } from './CprState/storage';
import { cprEventEmitter, EVENTS } from './cprEvents';
import { useResusContext } from '../Resus/ResusContext';
import { EmergencyProtocolsData, getHospitalData } from './PerHospitalResusConfig';
import { useTranslation } from 'react-i18next';

const getFormattedAge = (age: number | null, t: (key: string) => string): string => { // Improved typing for t
  if (age == null) {
    return "";
  }
  if (age === 0) return t('age.newborn');
  if (age === 0.23) return t('age.oneWeek');
  if (age === 1) return t('age.oneMonth');
  if (age === 2) return t('age.twoMonths');
  if (age === 12) return t('age.oneYear');
  if (age === 24) return t('age.twoYears');
  if (age < 24) {
    return `${age} ${t('age.months')}`;
  }
  return `${(age / 12)} ${t('age.years')}`;
};

export const getProtocolName = (emergencyProtocols: EmergencyProtocolsData, protocolId: string): string => {
  const protocol = emergencyProtocols.emergencyProtocols
    .flatMap((section) => section.protocols)
    .find((p) => p.id === protocolId);
  return protocol ? protocol.name : protocolId;
};

interface ArchivesListProps {
  onClose: () => void;
}

const ArchivesList: React.FC<ArchivesListProps> = ({ onClose }) => {
  const [archives, setArchives] = useState<any[]>([]);
  const { hospital } = useResusContext();
  const emergencyProtocols = getHospitalData(hospital).protocols;
  const { t } = useTranslation(['resus']);

  useEffect(() => {
    setArchives(loadArchivedCPRs());

    const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, () => {
      // Remove the archive parameter but maintain the hash
      const newUrl = new URL(window.location.href);
      const hash = newUrl.hash.split('?')[0];
      newUrl.hash = hash;
      window.history.pushState({}, '', newUrl.toString());
    });

    return () => unsubscribe();
  }, []);

  const handleArchiveClick = (archiveId: string) => {
    restoreArchivedCPR(archiveId);
    
    // Update URL maintaining the hash router format and reload
    const newUrl = new URL(window.location.href);
    const baseHash = newUrl.hash.split('?')[0];
    newUrl.hash = `${baseHash}${baseHash.includes('?') ? '&' : '?'}archive=${archiveId}`;
    window.history.pushState({}, '', newUrl.toString());
    window.location.reload();
  };

  const handleClearHistory = () => {
    clearHistory();
    onClose();
  };

  const getOutcomeText = (archive: any) => {
    if (!archive.outcome || !archive.outcome.status) return '';
    return archive.outcome.status === 'ROSC' ? t('archives.successOutcome') : t('archives.deathOutcome');
  };

  return (
    <div className="archives-overlay">
      <div className="archives-modal">
        <div className="archives-header">
          <h3>{t('buttons.previousProcedures')}</h3>
          <button onClick={onClose} className="close-button">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="archives-list">
          {archives.map((archive) => (
            <div 
              key={archive.id} 
              className="archive-item"
              onClick={() => handleArchiveClick(archive.id)}
            >
              {/*"the archive.resusStartTime | archive.archivedAt is for backward compatible. it should be just resusStartTime"*/}
              <span>{t('archives.patientFromDate')} {new Date(archive.resusStartTime || archive.archivedAt).toLocaleString('he-IL')}</span>
              <div className="archive-details">
                {archive.resusContext && (<div>
                  {archive.resusContext.age != null && <span>{t('patientInfo.age')}: {getFormattedAge(archive.resusContext.age, t)}</span>}
                  {archive.resusContext.weight && <span>{t('weight.label')}: {archive.resusContext.weight} {t('weight.unit')}</span>}
                  {archive.resusContext.protocol && (
                    <span>{t('protocols.title')}: {getProtocolName(emergencyProtocols, archive.resusContext.protocol)}</span>
                  )}
                </div>)}
                {archive.outcome && <span>{getOutcomeText(archive)}</span>}
              </div>
            </div>
          ))}
          {archives.length === 0 && (
            <div className="no-archives">{t('archives.noPreviousProcedures')}</div>
          )}
        </div>
        {archives.length > 0 && (
          <div className="archives-footer">
            <button onClick={handleClearHistory} className="clear-history-button">
              {t('archives.clearHistory')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchivesList;
